import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { createSpring, usePrefersReducedMotion } from './motion';
import { pressable } from './ui';

// The workspace control dock: a floating toolbar that pins under the site header, with one
// row per choice (period, exchange, pair). Each row is a horizontally scrolling segmented
// control whose thumb glides on a spring; an "All" button unfolds the full picker in place.

const HEADER_OFFSET = 66; // var(--header-h) + 10px gap

// ------------------------------------------------------------------ stuck detection

export function useStuck() {
  const ref = useRef(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const sentinel = ref.current;
    if (!sentinel || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting && entry.boundingClientRect.top < window.innerHeight / 2),
      { rootMargin: `-${HEADER_OFFSET + 1}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return [ref, stuck];
}

export const DockSentinel = styled.div`
  height: 1px;
  margin-bottom: -1px;
`;

// ------------------------------------------------------------------ shell & rows

export const DockShell = styled.div`
  position: sticky;
  top: ${HEADER_OFFSET}px;
  z-index: 20;
  border-radius: 20px;
  border: 1px solid var(--separator);
  background: var(--surface);
  box-shadow: var(--shadow-sm);
  transition:
    box-shadow 300ms ease,
    background-color 300ms ease;

  /* Pinned over content: becomes a translucent material that lets the page show through */
  ${(p) =>
    p.$stuck &&
    css`
      background: var(--material-strong);
      backdrop-filter: var(--material-filter);
      -webkit-backdrop-filter: var(--material-filter);
      box-shadow: var(--shadow-lg);
    `}
`;

const RowGroup = styled.div`
  & + & {
    border-top: 1px solid var(--separator);
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px 12px;
  padding: 8px 12px 8px 18px;

  @media (max-width: 700px) {
    grid-template-columns: 22px minmax(0, 1fr) auto;
    gap: 8px;
    padding: 4px 8px 4px 12px;
  }
`;

const RowLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: var(--text-secondary);

  svg {
    flex-shrink: 0;
    color: var(--text-tertiary);
  }

  @media (max-width: 700px) {
    span {
      display: none;
    }
  }
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

// Unfolds below its row, so the picker visibly comes from the control that opened it.
// grid-template-rows 0fr → 1fr animates to the content's natural height and can be reversed mid-way.
const Collapse = styled.div`
  display: grid;
  grid-template-rows: ${(p) => (p.$open ? '1fr' : '0fr')};
  transition: grid-template-rows 460ms var(--ease-out);

  > div {
    min-height: 0;
    overflow: hidden;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const PanelBody = styled.div`
  padding: 4px 18px 18px;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  transform: translate3d(0, ${(p) => (p.$open ? '0' : '-8px')}, 0);
  transition:
    opacity 300ms ease,
    transform 460ms var(--ease-out);

  @media (max-width: 700px) {
    padding: 4px 12px 14px;
  }

  @media (prefers-reduced-motion: reduce) {
    transform: none;
  }
`;

export function DockRow({ icon: Icon, label, strip, actions, panel, open = false, panelId }) {
  return (
    <RowGroup>
      <Row>
        <RowLabel title={label}>
          {Icon && <Icon size={18} />}
          <span>{label}</span>
        </RowLabel>
        <div style={{ minWidth: 0 }}>{strip}</div>
        {actions ? <RowActions>{actions}</RowActions> : <span />}
      </Row>
      {panel && (
        <Collapse $open={open}>
          <div id={panelId} {...(!open && { inert: '' })} aria-hidden={!open}>
            <PanelBody $open={open}>{panel}</PanelBody>
          </div>
        </Collapse>
      )}
    </RowGroup>
  );
}

// ------------------------------------------------------------------ expand button

const ExpandButtonBase = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 12px 0 10px;
  border: 0;
  border-radius: 980px;
  background: ${(p) => (p['aria-expanded'] ? 'var(--accent-tint)' : 'transparent')};
  color: var(--accent);
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  ${pressable}

  &:hover {
    background: var(--accent-tint);
  }

  svg {
    transition: transform 400ms var(--ease-out);
    transform: rotate(${(p) => (p['aria-expanded'] ? '180deg' : '0deg')});
  }

  @media (max-width: 700px) {
    width: 32px;
    padding: 0;
    justify-content: center;

    span {
      display: none;
    }
  }
`;

function ChevronDown(props) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function ExpandButton({ open, onClick, label, controls }) {
  return (
    <ExpandButtonBase type="button" aria-expanded={open} aria-controls={controls} onClick={onClick} title={label}>
      <span>{label}</span>
      <ChevronDown />
    </ExpandButtonBase>
  );
}

// ------------------------------------------------------------------ chip strip

const Scroller = styled.div`
  --fade-l: 0px;
  --fade-r: 0px;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  padding: 3px 0;
  -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 var(--fade-l), #000 calc(100% - var(--fade-r)), transparent 100%);
  mask-image: linear-gradient(90deg, transparent 0, #000 var(--fade-l), #000 calc(100% - var(--fade-r)), transparent 100%);

  &::-webkit-scrollbar {
    display: none;
  }

  &[data-fade-start='true'] {
    --fade-l: 28px;
  }

  &[data-fade-end='true'] {
    --fade-r: 36px;
  }

  @media (max-width: 700px) {
    padding: 2px 0;
  }

  &[data-dragging='true'] {
    cursor: grabbing;
    user-select: none;

    button {
      cursor: grabbing;
    }
  }
`;

const Track = styled.div`
  position: relative;
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border-radius: 12px;
  background: var(--fill);
  vertical-align: top;
`;

const Thumb = styled.span`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 0;
  width: 0;
  border-radius: 9px;
  background: var(--segment-active);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 0 0 0.5px rgba(0, 0, 0, 0.04);
  pointer-events: none;
  will-change: transform, width;
`;

export const Chip = styled.button`
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  padding: 0 13px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: -0.005em;
  white-space: nowrap;
  cursor: pointer;
  ${pressable}

  &:hover {
    color: var(--text);
  }

  &[aria-pressed='true'] {
    color: var(--text);
    font-weight: 600;
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }

  @media (max-width: 700px) {
    height: 30px;
    padding: 0 11px;
  }

  small {
    font-size: 0.75rem;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }
`;

export const StripHint = styled.span`
  display: inline-flex;
  align-items: center;
  height: 38px;
  padding: 0 4px;
  font-size: 0.875rem;
  color: var(--text-tertiary);
`;

// Mouse drag to scroll, with Apple's scroll deceleration (0.998 per ms) after release.
// Touch and trackpads keep their native scrolling.
function useDragScroll(ref, reduced) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let drag = null;
    let frame = null;

    const stopMomentum = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = null;
    };

    const onDown = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      stopMomentum();
      drag = { id: event.pointerId, x: event.clientX, left: el.scrollLeft, moved: false, samples: [{ x: event.clientX, t: event.timeStamp }] };
    };

    const onMove = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(dx) < 6) return; // hysteresis: a click stays a click
        drag.moved = true;
        el.setPointerCapture(event.pointerId);
        el.dataset.dragging = 'true';
      }
      el.scrollLeft = drag.left - dx;
      drag.samples.push({ x: event.clientX, t: event.timeStamp });
      if (drag.samples.length > 6) drag.samples.shift();
    };

    const onUp = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const { moved, samples } = drag;
      drag = null;
      delete el.dataset.dragging;
      if (!moved) return;

      // A drag must not also select the chip it ended on.
      const swallow = (clickEvent) => {
        clickEvent.stopPropagation();
        clickEvent.preventDefault();
      };
      el.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => el.removeEventListener('click', swallow, { capture: true }), 0);

      if (reduced) return;
      const first = samples[0];
      const lastSample = samples[samples.length - 1];
      const elapsed = lastSample.t - first.t;
      let velocity = elapsed > 0 ? (-(lastSample.x - first.x) / elapsed) * 1000 : 0; // px/s in scroll direction
      let previous = performance.now();
      const step = (now) => {
        const dt = now - previous;
        previous = now;
        velocity *= Math.pow(0.998, dt);
        el.scrollLeft += (velocity * dt) / 1000;
        frame = Math.abs(velocity) > 15 ? requestAnimationFrame(step) : null;
      };
      frame = requestAnimationFrame(step);
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('wheel', stopMomentum, { passive: true });
    el.addEventListener('touchstart', stopMomentum, { passive: true });
    return () => {
      stopMomentum();
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('wheel', stopMomentum);
      el.removeEventListener('touchstart', stopMomentum);
    };
  }, [ref, reduced]);
}

export function ChipStrip({ label, selectedKey, itemsKey, children }) {
  const scrollerRef = useRef(null);
  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const springRef = useRef(null);
  const placedRef = useRef(false);
  const centeredRef = useRef(false);
  const reduced = usePrefersReducedMotion();

  useDragScroll(scrollerRef, reduced);

  const updateFades = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.dataset.fadeStart = String(el.scrollLeft > 2);
    el.dataset.fadeEnd = String(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  // Glide the thumb to the selected chip; the first placement jumps, later ones spring.
  const placeThumb = useCallback(
    (animate) => {
      const track = trackRef.current;
      const thumb = thumbRef.current;
      if (!track || !thumb) return;
      const chip = track.querySelector('[aria-pressed="true"]');
      if (!chip) {
        thumb.style.opacity = '0';
        placedRef.current = false;
        return;
      }
      thumb.style.opacity = '1';
      if (!springRef.current) {
        springRef.current = createSpring(
          { x: chip.offsetLeft, w: chip.offsetWidth },
          ({ x, w }) => {
            thumb.style.transform = `translate3d(${x}px, 0, 0)`;
            thumb.style.width = `${w}px`;
          },
          { response: 0.38, damping: 1 }
        );
      }
      const target = { x: chip.offsetLeft, w: chip.offsetWidth };
      if (animate && placedRef.current && !reduced) springRef.current.to(target);
      else springRef.current.jump(target);
      placedRef.current = true;
    },
    [reduced]
  );

  // Bring the selected chip into the middle of the strip without moving the page.
  const centerSelected = useCallback(
    (smooth) => {
      const el = scrollerRef.current;
      const chip = trackRef.current && trackRef.current.querySelector('[aria-pressed="true"]');
      if (!el || !chip) return;
      const left = chip.offsetLeft - (el.clientWidth - chip.offsetWidth) / 2;
      el.scrollTo({ left: Math.max(0, left), behavior: smooth && !reduced ? 'smooth' : 'auto' });
    },
    [reduced]
  );

  useLayoutEffect(() => {
    placeThumb(true);
    updateFades();
  }, [selectedKey, itemsKey, placeThumb, updateFades]);

  // Only a new selection recenters — never a re-render, which would fight manual scrolling.
  useLayoutEffect(() => {
    centerSelected(centeredRef.current);
    centeredRef.current = true;
  }, [selectedKey, centerSelected]);

  useEffect(() => {
    const el = scrollerRef.current;
    const track = trackRef.current;
    if (!el || !track) return undefined;
    el.addEventListener('scroll', updateFades, { passive: true });
    let observer;
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(() => {
        placeThumb(false);
        updateFades();
      });
      observer.observe(el);
      observer.observe(track);
    }
    return () => {
      el.removeEventListener('scroll', updateFades);
      if (observer) observer.disconnect();
    };
  }, [placeThumb, updateFades]);

  useEffect(() => () => springRef.current && springRef.current.stop(), []);

  return (
    <Scroller ref={scrollerRef} role="group" aria-label={label}>
      <Track ref={trackRef}>
        <Thumb ref={thumbRef} aria-hidden="true" />
        {children}
      </Track>
    </Scroller>
  );
}
